import { Box, Paper } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { useQualityCheckRefresh } from "../hooks/useQualityCheckRefresh";
import {
	deleteBundleQualityCheck,
	getSawQualityChecks,
	type SawQualityCheck,
} from "../repositories/sawWriteRepository";
import { LastSawChecksTable } from "./last-checks-table/LastSawChecksTable";
import { CardPaperSx, CardTitle } from "./primitives";
import { ReclassifyBundleModal } from "./ReclassifyBundleModal";

interface Props {
	assetId: number | null;
	// Loads the check into the Bundle Quality Check form for correction.
	onEdit?: (check: SawQualityCheck) => void;
	// Lets the page drop a deleted check from its editing state.
	onDeleted?: (check: SawQualityCheck) => void;
	onError?: (e: Error) => void;
}

export const LastSawChecksCard = ({
	assetId,
	onEdit,
	onDeleted,
	onError,
}: Props) => {
	const queryClient = useQueryClient();
	const [reclassifyBundleId, setReclassifyBundleId] = useState<number | null>(
		null,
	);
	const [deletingCheck, setDeletingCheck] = useState<SawQualityCheck | null>(
		null,
	);

	const handleDelete = async (check: SawQualityCheck) => {
		try {
			await deleteBundleQualityCheck(check.QualityCheckId);
			await queryClient.invalidateQueries({ queryKey: ["sawQualityChecks"] });
			await queryClient.invalidateQueries({ queryKey: ["sawRecentBundles"] });
			onDeleted?.(check);
		} catch (e) {
			onError?.(e as Error);
		}
	};

	const { data: rows = [], isLoading } = useQuery<SawQualityCheck[], Error>({
		queryKey: ["sawQualityChecks", assetId],
		queryFn: () => getSawQualityChecks(assetId as number),
		enabled: assetId != null,
		refetchInterval: 30_000,
		onError,
		meta: {
			realtime: ["FAB.GetSawQualityChecks", "FAB.GetSawQualityChecksReport"],
		},
	});

	const handleReclassified = useQualityCheckRefresh();

	// Stable handlers — the grid rebuilds its columns when these change.
	const handleReclassify = useCallback(
		(check: SawQualityCheck) => setReclassifyBundleId(check.BundleId),
		[],
	);
	const handleEdit = useCallback(
		(check: SawQualityCheck) => onEdit?.(check),
		[onEdit],
	);

	return (
		<Paper elevation={1} sx={CardPaperSx}>
			<CardTitle title="Quality Checks - Current Shift" />
			<Box sx={{ flex: 1, minHeight: 160 }}>
				<LastSawChecksTable
					rows={rows}
					loading={isLoading}
					onReclassify={handleReclassify}
					onEdit={handleEdit}
					onDelete={setDeletingCheck}
				/>
			</Box>

			<ReclassifyBundleModal
				open={reclassifyBundleId != null}
				assetId={assetId}
				bundleId={reclassifyBundleId}
				onClose={() => setReclassifyBundleId(null)}
				onReclassified={handleReclassified}
			/>

			<ConfirmationDialogRaw
				open={deletingCheck != null}
				title="Delete quality check?"
				message="This will permanently delete the check and its measurements. Continue?"
				onClose={(value) => {
					const check = deletingCheck;
					setDeletingCheck(null);
					if (value === "OK" && check != null) void handleDelete(check);
				}}
			/>
		</Paper>
	);
};
